
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import UserControls from '@/components/UserControls';
import ConnectionStatusBadge from '@/components/ConnectionStatusBadge';
import VideoChat from '@/components/VideoChat';
import TextChat from '@/components/TextChat';
import { toast } from '@/utils/toast';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { ChatProvider, useChat } from '@/contexts/ChatContext';
import { handleError } from '@/utils/error-handler';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useDeviceSafetyCheck } from '@/hooks/use-device-safety-check';
import axios from 'axios';

const ChatContent = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [isTextChatOpen, setIsTextChatOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'excellent' | 'good' | 'poor'>('excellent');
  const [isChatEnded, setIsChatEnded] = useState(false);
  const [connectionInitiated, setConnectionInitiated] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef<number | null>(null);
  const roomIdRef = useRef<string | null>(null);
  
  const { 
    connectToRoom, 
    leaveRoom, 
    currentRoomId,
    connectionStatus: rtcConnectionStatus,
    toggleMicrophone,
    toggleVideo,
    isMicrophoneEnabled,
    isVideoEnabled,
    isSearchingForPartner,
    onlineUsers,
    activeRooms
  } = useChat();

  // Use our enhanced device safety check hook - Pass chat active state
  const { forceDisableDevices } = useDeviceSafetyCheck(!isChatEnded && connectionInitiated, isChatEnded);

  // Setup auto-hiding controls with improved touch support
  useEffect(() => {
    const hideControls = () => {
      setControlsVisible(false);
    };

    const showControls = () => {
      setControlsVisible(true);
      
      if (controlsTimerRef.current) {
        window.clearTimeout(controlsTimerRef.current);
      }
      
      // Set timer to hide controls after 3 seconds of inactivity
      controlsTimerRef.current = window.setTimeout(hideControls, 3000);
    };
    
    // Initial timer
    controlsTimerRef.current = window.setTimeout(hideControls, 3000);
    
    // Event listeners for user activity
    const handleMouseMove = () => showControls();
    const handleTouchStart = () => showControls();
    const handleKeyPress = () => showControls();
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      if (controlsTimerRef.current) {
        window.clearTimeout(controlsTimerRef.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      toast.error("Please log in to access chat");
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Connect to room and enable mic/camera when connection is initiated
  useEffect(() => {
    let isMounted = true;
    
    const connectToRandomRoom = async () => {
      if (!currentUser || connectionInitiated || currentRoomId || isChatEnded) return;
      
      setConnectionInitiated(true);
      
      try {
        if (!roomIdRef.current) {
          roomIdRef.current = `room-${Math.floor(Math.random() * 1000000)}`;
        }
        
        if (isMounted && roomIdRef.current) {
          // Enable camera and mic before connecting - IMMEDIATELY activate
          console.log("Enabling devices before connection");
          toggleVideo(true);
          toggleMicrophone(true);
          
          // Add a small delay to ensure devices are properly initialized
          setTimeout(async () => {
            if (!isMounted) return;
            
            // Verify devices are enabled before continuing
            if (!isVideoEnabled) {
              console.log("Camera not enabled, retrying...");
              toggleVideo(true);
            }
            
            if (!isMicrophoneEnabled) {
              console.log("Microphone not enabled, retrying...");
              toggleMicrophone(true);
            }
            
            const success = await connectToRoom(roomIdRef.current!);
            
            if (success && isMounted) {
              if (!isSearchingForPartner) {
                toast.success("Chat partner found!");
              }
              
              // Log chat session start to database
              try {
                await axios.post('http://localhost:3001/api/chat-sessions', {
                  userId: currentUser.uid,
                  sessionStart: new Date().toISOString(),
                  roomId: roomIdRef.current
                });
              } catch (error) {
                console.error("Failed to log chat session start:", error);
                // Don't show error to user, just log it
              }
            }
          }, 500); // Reduced delay for faster device activation
        }
      } catch (error) {
        console.error("Error connecting to room:", error);
        if (isMounted) {
          setConnectionInitiated(false);
          handleError(error, {
            customMessage: "Failed to connect to chat. Please try again.",
            retryCallback: () => setConnectionInitiated(false)
          });
        }
      }
    };
    
    if (currentUser && !currentRoomId && !isChatEnded && !connectionInitiated) {
      connectToRandomRoom();
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, connectToRoom, currentRoomId, isChatEnded, connectionInitiated, isSearchingForPartner, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled]);
  
  // Clean up when component unmounts - ensure mic and camera are turned off IMMEDIATELY
  useEffect(() => {
    return () => {
      console.log("Chat component unmounting - cleaning up");
      leaveRoom();
      
      // Immediately force off both devices
      toggleMicrophone(false);
      toggleVideo(false);
      
      // Additional safety check through the safety hook
      forceDisableDevices();

      // End the chat session in the database
      if (currentUser && roomIdRef.current) {
        try {
          axios.put(`http://localhost:3001/api/chat-sessions/${roomIdRef.current}`, {
            userId: currentUser.uid,
            sessionEnd: new Date().toISOString()
          }).catch(error => {
            console.error("Failed to log chat session end:", error);
            // Don't show error to user, just log it
          });
        } catch (error) {
          console.error("Error logging chat session end:", error);
        }
      }
    };
  }, [leaveRoom, toggleMicrophone, toggleVideo, currentUser, forceDisableDevices]);
  
  useEffect(() => {
    if (rtcConnectionStatus === 'connected') {
      setConnectionStatus('excellent');
    } else if (rtcConnectionStatus === 'connecting') {
      setConnectionStatus('good');
    } else {
      setConnectionStatus('poor');
    }
  }, [rtcConnectionStatus]);

  const handleFindNext = useCallback(async () => {
    leaveRoom();
    setConnectionInitiated(false);
    
    const connectToNewRoom = async () => {
      setConnectionInitiated(true);
      
      roomIdRef.current = `room-${Math.floor(Math.random() * 1000000)}`;
      
      try {
        // Ensure mic and camera are on for new connection - IMMEDIATELY activate
        console.log("Enabling devices for new connection");
        toggleVideo(true);
        toggleMicrophone(true);
        
        // Verify devices are activated
        setTimeout(() => {
          if (!isVideoEnabled) {
            console.log("Camera not enabled for new connection, retrying...");
            toggleVideo(true);
          }
          
          if (!isMicrophoneEnabled) {
            console.log("Microphone not enabled for new connection, retrying...");
            toggleMicrophone(true);
          }
        }, 200);
        
        // Add a short delay to ensure devices have time to initialize
        setTimeout(async () => {
          if (roomIdRef.current) {
            const success = await connectToRoom(roomIdRef.current);
            
            if (success && !isSearchingForPartner) {
              toast.success("New chat partner found!");
              
              // Log new chat session
              if (currentUser) {
                try {
                  await axios.post('http://localhost:3001/api/chat-sessions', {
                    userId: currentUser.uid,
                    sessionStart: new Date().toISOString(),
                    roomId: roomIdRef.current
                  });
                } catch (error) {
                  console.error("Failed to log new chat session:", error);
                  // Don't show error to user, just log it
                }
              }
            }
          }
        }, 500); // Reduced delay for faster experience
      } catch (error) {
        console.error("Error connecting to new room:", error);
        setConnectionInitiated(false);
        handleError(error, {
          customMessage: "Failed to find a new partner. Please try again.",
          retryCallback: handleFindNext
        });
      }
    };
    
    connectToNewRoom();
  }, [leaveRoom, connectToRoom, isSearchingForPartner, toggleMicrophone, toggleVideo, currentUser, isMicrophoneEnabled, isVideoEnabled]);

  const handleEndChat = useCallback(() => {
    setIsChatEnded(true);
    leaveRoom();
    
    // IMMEDIATELY turn off camera and mic
    console.log("Chat ended - immediately disabling all devices");
    toggleMicrophone(false);
    toggleVideo(false);
    
    // Use the safety hook's force disable as a backup
    forceDisableDevices();
    
    // End chat session in database
    if (currentUser && roomIdRef.current) {
      axios.put(`http://localhost:3001/api/chat-sessions/${roomIdRef.current}`, {
        userId: currentUser.uid,
        sessionEnd: new Date().toISOString()
      }).catch(error => {
        console.error("Failed to log chat session end:", error);
      });
    }
    
    // Double-check devices are turned off
    setTimeout(() => {
      if (isMicrophoneEnabled) {
        console.warn("Microphone still enabled after chat ended. Turning off again...");
        toggleMicrophone(false);
      }
      
      if (isVideoEnabled) {
        console.warn("Camera still enabled after chat ended. Turning off again...");
        toggleVideo(false);
      }
    }, 300);
    
    const timer = setTimeout(() => {
      navigate('/');
    }, 3000);
    
    return () => {
      clearTimeout(timer);
    };
  }, [leaveRoom, navigate, toggleMicrophone, toggleVideo, isMicrophoneEnabled, isVideoEnabled, currentUser, forceDisableDevices]);

  const toggleTextChat = useCallback(() => {
    setIsTextChatOpen(prev => !prev);
  }, []);

  const isActive = rtcConnectionStatus === 'connected' || isSearchingForPartner;

  return (
    <div className={cn(
      "relative flex h-screen overflow-hidden touch-action-manipulation",
      isDark ? "bg-[#0a0e1e]" : "bg-gray-900"
    )}>
      <Helmet>
        <title>Live Chat | Chatrix</title>
        <meta name="description" content="Connect with people around the world through video and text chat" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      
      <AnimatePresence>
        {controlsVisible && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={cn(
              "absolute top-0 left-0 right-0 p-3 flex justify-between items-center z-50 backdrop-blur-md",
              isDark ? "bg-[#0a0e1e]/80" : "bg-gray-900/80"
            )}
          >
            <button 
              onClick={() => {
                // Make sure to turn off camera and mic before exiting
                toggleMicrophone(false);
                toggleVideo(false);
                navigate('/');
              }}
              className="p-2 rounded-full flex items-center space-x-2 hover:bg-white/10 transition-all text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              <span className="text-sm font-medium">Exit</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="relative flex-1 flex items-center justify-center mt-12">
        {isChatEnded ? (
          <div className="text-center text-white">
            <h2 className="text-2xl font-bold mb-4 font-sans">Chat Ended</h2>
            <p className="mb-6 font-sans">Returning to homepage...</p>
          </div>
        ) : (
          <VideoChat isActive={isActive} />
        )}
        
        {isActive && !isChatEnded && controlsVisible && (
          <div className="absolute top-4 right-4 z-30">
            <ConnectionStatusBadge status={connectionStatus} minimal={true} />
          </div>
        )}
      </div>

      <AnimatePresence>
        {isTextChatOpen && (isActive || isSearchingForPartner) && !isChatEnded && (
          <TextChat 
            isOpen={true} 
            key="text-chat"
            soundEffects={true}
          />
        )}
      </AnimatePresence>
      
      {!isChatEnded && (
        <UserControls
          onToggleTextChat={toggleTextChat}
          onNext={handleFindNext}
          onEnd={handleEndChat}
          isTextChatActive={isTextChatOpen}
          position="bottom-center"
          isVisible={controlsVisible}
        />
      )}
    </div>
  );
};

const Chat = () => {
  return (
    <ChatProvider>
      <ChatContent />
    </ChatProvider>
  );
};

export default Chat;
